// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file BankingTransfer.tsx
// @author Arlinda Peixoto
// @company Pagali
// @description Página de transferências bancárias
// @date 2026-01-28
// ------------------------------------------------------------

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { bankingService } from '../services';
import { useToast } from '../contexts/ToastContext';
import { Card, LoaderComponent, Button, Modal } from '../components/ui';
import type {
  Bank,
  TransferLimits,
  TransferCorridor,
  ExchangeQuote,
  Transfer,
} from '../types';
import {
  Send,
  Building2,
  Globe,
  User,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  RefreshCw,
} from 'lucide-react';

type TransferType = 'p2p' | 'bank' | 'international';

interface TabProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

const Tab: React.FC<TabProps> = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`flex-1 flex items-center justify-center gap-2 py-3 border-b-2 transition-colors ${
      active
        ? 'border-blue-600 text-blue-600'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >
    {icon}
    <span className="font-medium">{label}</span>
  </button>
);

const BankingTransfer: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [activeTab, setActiveTab] = useState<TransferType>('p2p');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Data
  const [banks, setBanks] = useState<Bank[]>([]);
  const [limits, setLimits] = useState<TransferLimits | null>(null);
  const [corridors, setCorridors] = useState<TransferCorridor[]>([]);
  const [quote, setQuote] = useState<ExchangeQuote | null>(null);

  // P2P Form
  const [recipientPhone, setRecipientPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [pin, setPin] = useState('');

  // Bank Form
  const [selectedBank, setSelectedBank] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [holderName, setHolderName] = useState('');

  // International Form
  const [selectedCorridor, setSelectedCorridor] = useState('');
  const [recipientCountry, setRecipientCountry] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientBankCode, setRecipientBankCode] = useState('');
  const [recipientAccountNumber, setRecipientAccountNumber] = useState('');
  const [purpose, setPurpose] = useState('');

  // Result
  const [result, setResult] = useState<Transfer | null>(null);
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Transferir');
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'international' && selectedCorridor && amount) {
      getQuote();
    }
  }, [selectedCorridor, amount]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [banksData, limitsData, corridorsData] = await Promise.allSettled([
        bankingService.getBanks(),
        bankingService.getTransferLimits(),
        bankingService.getCorridors(),
      ]);

      if (banksData.status === 'fulfilled') setBanks(banksData.value);
      if (limitsData.status === 'fulfilled') setLimits(limitsData.value);
      if (corridorsData.status === 'fulfilled') setCorridors(corridorsData.value);
    } catch (err) {
      showError('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const getQuote = async () => {
    if (!selectedCorridor || !amount) return;

    try {
      const quoteData = await bankingService.getExchangeQuote(selectedCorridor, parseFloat(amount));
      setQuote(quoteData);
    } catch (err) {
      console.error('Error getting quote:', err);
    }
  };

  const formatAmount = (value: number | undefined | null): string => {
    const safe = typeof value === 'number' && !isNaN(value) ? value : 0;
    return new Intl.NumberFormat('pt-CV', {
      style: 'currency',
      currency: 'CVE',
      minimumFractionDigits: 0,
    }).format(safe);
  };

  const resetForm = () => {
    setRecipientPhone('');
    setAmount('');
    setDescription('');
    setPin('');
    setSelectedBank('');
    setAccountNumber('');
    setHolderName('');
    setSelectedCorridor('');
    setRecipientCountry('');
    setRecipientName('');
    setRecipientBankCode('');
    setRecipientAccountNumber('');
    setPurpose('');
    setQuote(null);
  };

  const validateForm = (): boolean => {
    if (!amount || parseFloat(amount) <= 0) {
      showError('Insira um valor válido');
      return false;
    }

    if (!pin || pin.length < 4) {
      showError('Insira o PIN de 4 dígitos');
      return false;
    }

    if (limits) {
      const amountNum = parseFloat(amount);
      if (amountNum > limits.perTransaction) {
        showError(`Valor máximo por transação: ${formatAmount(limits.perTransaction)}`);
        return false;
      }
      if (amountNum > limits.daily.limit - limits.daily.used) {
        showError(`Limite diário disponível: ${formatAmount(limits.daily.limit - limits.daily.used)}`);
        return false;
      }
    }

    switch (activeTab) {
      case 'p2p':
        if (!recipientPhone) {
          showError('Insira o número do destinatário');
          return false;
        }
        break;
      case 'bank':
        if (!selectedBank || !accountNumber || !holderName) {
          showError('Preencha todos os campos obrigatórios');
          return false;
        }
        break;
      case 'international':
        if (!selectedCorridor || !recipientName || !recipientCountry) {
          showError('Preencha todos os campos obrigatórios');
          return false;
        }
        break;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      let transferResult: Transfer;

      switch (activeTab) {
        case 'p2p':
          transferResult = await bankingService.transferP2P({
            recipientPhone,
            amount: parseFloat(amount),
            description,
            pin,
          });
          break;
        case 'bank':
          transferResult = await bankingService.transferBank({
            bankCode: selectedBank,
            accountNumber,
            holderName,
            amount: parseFloat(amount),
            description,
            pin,
          });
          break;
        case 'international':
          transferResult = await bankingService.transferInternational({
            corridorId: selectedCorridor,
            recipientName,
            recipientCountry,
            recipientBankCode,
            recipientAccountNumber,
            amount: parseFloat(amount),
            currency: 'CVE',
            purpose,
            pin,
          });
          break;
      }

      setResult(transferResult!);
      setShowResult(true);
      showSuccess('Transferência realizada com sucesso!');
      resetForm();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Erro na transferência');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoaderComponent />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/banking')}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ChevronLeft size={24} />
        </button>
        <h1 className="text-2xl font-bold">Transferir</h1>
      </div>

      {/* Limits Card */}
      {limits && (
        <Card className="bg-blue-50 border-blue-200">
          <div className="p-4">
            <p className="text-sm text-blue-800 mb-2">Limites disponíveis:</p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-600">Diário:</span>{' '}
                <span className="font-semibold text-blue-900">
                  {formatAmount(limits.daily.limit - limits.daily.used)}
                </span>
              </div>
              <div>
                <span className="text-blue-600">Por transação:</span>{' '}
                <span className="font-semibold text-blue-900">
                  {formatAmount(limits.perTransaction)}
                </span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <Card>
        <div className="flex border-b">
          <Tab
            active={activeTab === 'p2p'}
            onClick={() => setActiveTab('p2p')}
            icon={<User size={20} />}
            label="Pagali"
          />
          <Tab
            active={activeTab === 'bank'}
            onClick={() => setActiveTab('bank')}
            icon={<Building2 size={20} />}
            label="Banco"
          />
          <Tab
            active={activeTab === 'international'}
            onClick={() => setActiveTab('international')}
            icon={<Globe size={20} />}
            label="Internacional"
          />
        </div>

        <div className="p-6 space-y-4">
          {/* P2P Form */}
          {activeTab === 'p2p' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número do destinatário
                </label>
                <input
                  type="tel"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  placeholder="+238 9XX XXXX"
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </>
          )}

          {/* Bank Form */}
          {activeTab === 'bank' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Banco de destino
                </label>
                <select
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value)}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selecione o banco</option>
                  {banks.map((bank) => (
                    <option key={bank.code} value={bank.code}>
                      {bank.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Número da conta
                </label>
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="Número da conta"
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do titular
                </label>
                <input
                  type="text"
                  value={holderName}
                  onChange={(e) => setHolderName(e.target.value)}
                  placeholder="Nome completo"
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </>
          )}

          {/* International Form */}
          {activeTab === 'international' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  País de destino
                </label>
                <select
                  value={selectedCorridor}
                  onChange={(e) => {
                    setSelectedCorridor(e.target.value);
                    const corridor = corridors.find((c) => c.id === e.target.value);
                    if (corridor) setRecipientCountry(corridor.destinationCountry);
                  }}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selecione o país</option>
                  {corridors.map((corridor) => (
                    <option key={corridor.id} value={corridor.id}>
                      {corridor.destinationCountry} ({corridor.currency})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do beneficiário
                </label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Nome completo"
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Código do banco
                  </label>
                  <input
                    type="text"
                    value={recipientBankCode}
                    onChange={(e) => setRecipientBankCode(e.target.value)}
                    placeholder="SWIFT/BIC"
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Número da conta
                  </label>
                  <input
                    type="text"
                    value={recipientAccountNumber}
                    onChange={(e) => setRecipientAccountNumber(e.target.value)}
                    placeholder="IBAN/Conta"
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Finalidade
                </label>
                <select
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selecione</option>
                  <option value="family_support">Apoio Familiar</option>
                  <option value="education">Educação</option>
                  <option value="medical">Saúde</option>
                  <option value="business">Negócios</option>
                  <option value="travel">Viagem</option>
                  <option value="other">Outro</option>
                </select>
              </div>
            </>
          )}

          {/* Common Fields */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valor (CVE)
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xl font-semibold"
            />
          </div>

          {/* Exchange Quote for International */}
          {activeTab === 'international' && quote && (
            <Card className="bg-gray-50">
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600">Taxa de câmbio</span>
                  <button onClick={getQuote} className="text-blue-600">
                    <RefreshCw size={16} />
                  </button>
                </div>
                <div className="text-lg font-semibold">
                  1 ECV = {quote.exchangeRate.toFixed(4)} {quote.destinationCurrency}
                </div>
                <div className="mt-2 pt-2 border-t grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Destinatário recebe:</span>
                    <p className="font-semibold">
                      {quote.destinationAmount.toFixed(2)} {quote.destinationCurrency}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Taxa:</span>
                    <p className="font-semibold">{formatAmount(quote.fee)}</p>
                  </div>
                </div>
              </div>
            </Card>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Descrição (opcional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Motivo da transferência"
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              PIN de segurança
            </label>
            <input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="****"
              maxLength={4}
              className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center text-xl tracking-widest"
            />
          </div>

          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2"
          >
            {submitting ? (
              <LoaderComponent />
            ) : (
              <>
                <Send size={20} />
                Transferir {amount && formatAmount(parseFloat(amount))}
              </>
            )}
          </Button>
        </div>
      </Card>

      {/* Result Modal */}
      <Modal isOpen={showResult} onClose={() => setShowResult(false)} title="Transferência">
        <div className="text-center py-6">
          {result?.status === 'completed' || result?.status === 'pending' ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                {result.status === 'completed' ? 'Transferência Concluída!' : 'Transferência em Processamento'}
              </h3>
              <p className="text-gray-600 mb-4">
                {formatAmount(result.amount)} para {result.recipientName}
              </p>
              <p className="text-sm text-gray-500">ID: {result.id}</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle size={32} className="text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Transferência Falhou</h3>
              <p className="text-gray-600">Por favor, tente novamente.</p>
            </>
          )}
          <Button
            onClick={() => {
              setShowResult(false);
              setResult(null);
            }}
            className="mt-6 px-8 py-2 bg-blue-600 text-white rounded-lg"
          >
            Fechar
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default BankingTransfer;
